<script setup lang="ts">
import { cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { ICONS, type IconName } from "@/design/icons";
import Icon from "./Icon.vue";

/**
 * Design-system primitive: tokens only, no business knowledge, no network.
 *
 * UMA COISA QUE A PESSOA PRECISA DE SABER ANTES DE CONTINUAR — no lugar onde ela
 * está a olhar.
 *
 * ── POR QUE NÃO É UM AVISO FLUTUANTE ──────────────────────────────────────
 *
 * `Toast` diz o que ACABOU de acontecer e desaparece. Isto diz o que É verdade
 * enquanto a tela estiver aberta: "esta regra está ligada mas o relógio dela
 * está pausado", "este canal não está conectado". Um aviso que some depois de
 * três segundos é a forma errada de contar uma coisa permanente — quem chegar
 * meio minuto depois nunca a vê.
 *
 * ── O PAPEL DEPENDE DO TOM ────────────────────────────────────────────────
 *
 * `danger` é `role="alert"`: interrompe quem lê a tela, porque é uma recusa que
 * bloqueia o que a pessoa estava a fazer. Os outros são `status`, que é anunciado
 * na primeira pausa — um aviso de contexto não vale a pena roubar a palavra a
 * meio de uma frase.
 */
const box = cva("flex items-start gap-2 rounded-xl border px-3 py-2 text-xs", {
	variants: {
		tone: {
			info: "border-border bg-bg-muted text-fg-muted",
			warning: "border-warning/40 bg-warning-subtle text-warning",
			danger: "border-danger/40 bg-danger-subtle text-danger",
			success: "border-success/40 bg-success-subtle text-success",
		},
	},
	defaultVariants: { tone: "info" },
});

const props = withDefaults(
	defineProps<{
		tone?: "info" | "warning" | "danger" | "success";
		/** Um ícone à esquerda, do `icons.ts` — nunca um glifo. Decorativo: a frase tem de bastar sem ele. */
		icon?: IconName;
		/** O título, quando a frase é longa o bastante para precisar de um. */
		title?: string;
		class?: string;
	}>(),
	{ tone: "info", class: "" },
);

const classes = computed(() => twMerge(box({ tone: props.tone }), props.class));
// Generated screens (`BxAlert`) are untyped at runtime and may still pass a glyph: kept for them only.
const isIconName = (name: string): name is IconName => name in ICONS;
</script>

<template>
	<div :class="classes" :role="tone === 'danger' ? 'alert' : 'status'">
		<Icon v-if="icon && isIconName(icon)" :name="icon" class="mt-px size-3.5" />
		<span v-else-if="icon" aria-hidden="true" class="leading-4">{{ icon }}</span>
		<div class="min-w-0 flex-1">
			<p v-if="title" class="font-medium">{{ title }}</p>
			<div :class="title ? 'mt-0.5' : ''"><slot /></div>
		</div>
	</div>
</template>
